import React, { Fragment, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import useRequests from "../../../utils/useRequests";
import { Spinner, Row, Col, Card, Badge } from "react-bootstrap";
import Snackbars from "../../../utils/Snackbars";

const VenueDetails = () => {
    const { v_id } = useParams();
    const [loading, setLoading] = useState(true);
    const { getRequest } = useRequests();
    const [detail, setDetail] = useState({
        name: "",
        address: "",
        city: "",
        capacity: "",
        description: "",
        status: "Enable",
    });
    const [seatCategories, setSeatCategories] = useState([]);
    const [AlertData, setAlertData] = React.useState({
        variant: "",
        message: ""
    });

    const resetAlertData = () => {
        setTimeout(() => {
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: "",
                message: ""
            }));
        }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
    };

    const fetchVenueDetails = async () => {
        setLoading(true);
        try {
            const response = await getRequest(`venue-details/${v_id}`);
            if (response && response.code === 200) {
                const data = response.data;
                setDetail({
                    name: data.name || "",
                    address: data.address || "",
                    city: data.city || "",
                    capacity: data.capacity || "",
                    description: data.description || "",
                    status: data.status || "Enable",
                });
            } else {
                setAlertData({
                    variant: "error",
                    message: response?.message || "Failed to fetch venue details"
                });
                resetAlertData();
            }
        } catch (error) {
            console.error("Error fetching venue details:", error);
            setAlertData({
                variant: "error",
                message: "An error occurred while fetching venue details"
            });
            resetAlertData();
        }
        setLoading(false);
    };

    const fetchSeatCategories = async () => {
        try {
            const response = await getRequest(`venue-seat-categories/${v_id}`);
            if (response && response.code === 200) {
                setSeatCategories(response.data);
            } else {
                console.error("Failed to fetch seat categories:", response?.message);
            }
        } catch (error) {
            console.error("Error fetching seat categories:", error);
        }
    };

    useEffect(() => {
        fetchVenueDetails();
        fetchSeatCategories();
    }, [v_id]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        return status === 'Enable' ? 'success' : 'danger';
    };

    const getTotalSeats = () => {
        return seatCategories.reduce((total, category) => total + parseInt(category.total_seats || 0), 0);
    };

    return (
        <Fragment>
            <div className="d-sm-flex d-block justify-content-between align-items-center mb-4">
                <h2 className="text-black font-w600 mb-0">
                    <i className="flaticon-381-location me-2"></i>
                    Venue Details
                </h2>
                {AlertData.message !== "" && (
                    <Snackbars variant={AlertData.variant} message={AlertData.message} />
                )}
                <div className="d-flex gap-2">
                    <Link to={`/venue-edit/${v_id}`} className="btn btn-rounded btn-warning">
                        <i className="fa-regular fa-pen-to-square me-2"></i>
                        Edit Venue
                    </Link>
                    <Link to={`/venue-seat-categories/${v_id}`} className="btn btn-rounded btn-info">
                        <i className="flaticon-381-controls me-2"></i>
                        Manage Seats
                    </Link>
                    <Link to="/venue-list" className="btn btn-rounded btn-primary">
                        <i className="fa-solid fa-arrow-left me-2"></i>
                        Back to List
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="text-center" style={{ marginTop: "100px" }}>
                    <Spinner animation="border" variant="primary" size="lg" />
                    <p className="mt-3">Loading venue details...</p>
                </div>
            ) : (
                <Row>
                    {/* Main Venue Information */}
                    <Col lg={8}>
                        <Card className="mb-4">
                            <Card.Header className="bg-primary" style={{ color: 'white' }}>
                                <h4 className="card-title mb-0" style={{ color: 'white' }}>
                                    <i className="flaticon-381-location me-2"></i>
                                    {detail.name}
                                </h4>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="venue-info-item mb-3">
                                            <h6 className="text-muted mb-1">
                                                <i className="fa-solid fa-city me-2"></i>
                                                City
                                            </h6>
                                            <p className="mb-0 fw-bold">{detail.city}</p>
                                        </div>
                                        <div className="venue-info-item mb-3">
                                            <h6 className="text-muted mb-1">
                                                <i className="fa-solid fa-map-marker-alt me-2"></i>
                                                Address
                                            </h6>
                                            <p className="mb-0">{detail.address}</p>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="venue-info-item mb-3">
                                            <h6 className="text-muted mb-1">
                                                <i className="fa-solid fa-users me-2"></i>
                                                Total Capacity
                                            </h6>
                                            <p className="mb-0 fw-bold text-primary fs-5">{detail.capacity}</p>
                                        </div>
                                        <div className="venue-info-item mb-3">
                                            <h6 className="text-muted mb-1">
                                                <i className="fa-solid fa-toggle-on me-2"></i>
                                                Status
                                            </h6>
                                            <Badge bg={getStatusColor(detail.status)} className="fs-6">
                                                {detail.status}
                                            </Badge>
                                        </div>
                                    </Col>
                                </Row>
                                
                                <div className="venue-info-item mt-3">
                                    <h6 className="text-muted mb-2">
                                        <i className="fa-solid fa-info-circle me-2"></i>
                                        Description
                                    </h6>
                                    <p className="mb-0 text-justify">{detail.description}</p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Quick Stats */}
                    <Col lg={4}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="card-title mb-0">
                                    <i className="fa-solid fa-chart-bar me-2"></i>
                                    Quick Stats
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="stats-item d-flex justify-content-between align-items-center mb-3 p-3 border rounded">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Categories</h6>
                                        <h4 className="mb-0 text-info">{seatCategories.length}</h4>
                                    </div>
                                    <i className="flaticon-381-controls text-info fs-2"></i>
                                </div>
                                
                                <div className="stats-item d-flex justify-content-between align-items-center mb-3 p-3 border rounded">
                                    <div>
                                        <h6 className="text-muted mb-1">Configured Seats</h6>
                                        <h4 className="mb-0 text-success">{getTotalSeats()}</h4>
                                    </div>
                                    <i className="fa-solid fa-chair text-success fs-2"></i>
                                </div>

                                <div className="stats-item d-flex justify-content-between align-items-center p-3 border rounded">
                                    <div>
                                        <h6 className="text-muted mb-1">Remaining Capacity</h6>
                                        <h4 className="mb-0 text-warning">{detail.capacity - getTotalSeats()}</h4>
                                    </div>
                                    <i className="fa-solid fa-exclamation-triangle text-warning fs-2"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Seat Categories */}
                    {seatCategories.length > 0 && (
                        <Col lg={12}>
                            <Card>
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">
                                        <i className="flaticon-381-controls me-2"></i>
                                        Seat Categories ({seatCategories.length})
                                    </h5>
                                    <Link 
                                        to={`/venue-seat-categories/${v_id}`} 
                                        className="btn btn-rounded btn-primary"
                                    >
                                        <i className="fa-solid fa-cog me-1"></i>
                                        Manage
                                    </Link>
                                </Card.Header>
                                <Card.Body>
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>
                                                        <i className="fa-solid fa-tag me-1"></i>
                                                        Category Name
                                                    </th>
                                                    <th>
                                                        <i className="fa-solid fa-code me-1"></i>
                                                        Code
                                                    </th>
                                                    <th>
                                                        <i className="fa-solid fa-chair me-1"></i>
                                                        Total Seats
                                                    </th>
                                                    <th>
                                                        <i className="fa-solid fa-list me-1"></i>
                                                        Seat Numbers (Preview)
                                                    </th>
                                                    <th>
                                                        <i className="fa-solid fa-toggle-on me-1"></i>
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {seatCategories.map((category) => (
                                                    <tr key={category.sc_id}>
                                                        <td className="fw-bold">{category.category_name}</td>
                                                        <td>
                                                            <Badge bg="secondary" className="fw-bold">
                                                                {category.category_code}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info fs-6">
                                                                {category.total_seats}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {category.seats && category.seats.length > 50 
                                                                    ? `${category.seats.substring(0, 50)}...` 
                                                                    : category.seats
                                                                }
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <Badge bg={getStatusColor(category.status)}>
                                                                {category.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}

                    {seatCategories.length === 0 && (
                        <Col lg={12}>
                            <Card>
                                <Card.Body className="text-center py-5">
                                    <i className="flaticon-381-controls text-muted" style={{ fontSize: "4rem" }}></i>
                                    <h4 className="mt-3 text-muted">No Seat Categories Found</h4>
                                    <p className="text-muted mb-4">
                                        This venue doesn't have any seat categories configured yet.
                                    </p>
                                    <Link 
                                        to={`/venue-seat-categories/${v_id}`} 
                                        className="btn btn-primary"
                                    >
                                        <i className="fa-solid fa-plus me-2"></i>
                                        Add Seat Categories
                                    </Link>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}
                </Row>
            )}
        </Fragment>
    );
};

export default VenueDetails;